import Bugsnag from "@bugsnag/js"
import { nextTick } from "vue"
import { createStore } from "vuex"
import { getBrowserPreferredLocales } from "~/lib/i18nUtils"
import { loadLocale } from "~/i18n"
import { ApiError, defaultCtx } from "~/lib/AjaxCtx"
import { leaveBreadcrumb } from "~/lib/bugsnag"
import {
    HEADER_HEIGHT,
    MIN_NOTIFICATION_LENGTH_TO_SHOW_CLOSE_BUTTON,
    NOTICATION_CLOSING_TIME,
} from "~/lib/constants"
import { subscribe, unsubscribe } from "~/lib/subscriptions"
import { parseQueryString, uuid } from "~/lib/utils"
import { logoutRedirection } from "~/lib/vueUtils"
import bus from "~/bus"

import calendarStore from "~/modules/calendar/calendarStore"
import communicationStore from "~/modules/communication/communicationStore"
import finderStore from "~/modules/finder/finderStore"
import listStore from "~/modules/list/listStore"
import LoginService from "~/modules/login/LoginService"
import authenticationStore from "~/modules/login/authenticationStore"
import cookieStore from "~/modules/login/cookieStore"
import messageStore from "~/modules/messages/messageStore"
import newsStore from "~/modules/news/newsStore"
import lmsStore from "~/modules/plugins/lmsStore"
import profileStore from "~/modules/profile/profileStore"
import supervisorStore from "~/modules/supervisor/supervisorStore"
import pushNotificationStore from "~/pushNotificationStore"
import selectionStore from "~/selectionStore"
import structureStore from "~/structureStore"
import themeStore from "~/themeStore"
import adminStore from "./admin"
import {
    AuthorizationHeaderPlugin,
    GoogleAnalyticsPlugin,
    SWBearerTokenPlugin,
    SWPushNotificationsPlugin,
} from "./plugins"

export function makeDefaultState() {
    return {
        tabId: uuid(), // Used to uniquely identify this tab. Should not change during the entire live of the application
        versionOutdated: false,
        currTop: HEADER_HEIGHT,
        platform: {
            alertFields: [],
            aliveInterval: 60, // seconds
            allowedFiles: [],
            backgroundImages: {
                desktop: [],
                mobile: [],
            },
            biDashboardUrl: null,
            certificate: null,
            code: "",
            defaultEntryPoint: null,
            entitiesFinder: [],
            hasBookmarks: false,
            hasCalendar: false,
            hasMailbox: false,
            hasSupportMailbox: false,
            installEvent: null,
            loginStatus: {},
            name: "",
            navigationHelpUrl: "",
            originalUser: null,
            platformUrl: "",
            showInstallPrompt: false,
            translationsHash: "",
            unreadMessagesInterval: 120, // seconds
            v2AdminUrl: "",
            v2BIReportsUrl: "",
            v2CampusOknUrl: "",
            v2DirectoryUrl: "",
            v2ForumModeratorUrl: "",
            v2ObserverUrl: "",
            v2ProviderUrl: "",
            v2TeacherUrl: "",
            v2Url: "",
        },
        featureFlags: {},
        configs: {},
        notifications: [],
        navigation: [],
        lastSpace: null,
        bearerToken: undefined,
    }
}

export const getters = {
    allowedExtensions: state => {
        return state.platform.allowedFiles.map(i => i.extension)
    },

    allowedMimeTypes: state => {
        return state.platform.allowedFiles.map(i => i.mimetypeName).join(",")
    },

    currentUser: (_state, getters) => {
        return getters["profile/currentUser"]
    },

    hasRequiredAlertFields: state => {
        const { alertFields = [] } = state.platform
        return alertFields.some(field => field.required)
    },

    timeZone: (_state, getters) => {
        const currentUser = getters["profile/currentUser"]
        if (currentUser?.timeZone) return currentUser.timeZone
        // If the timezone is an empty string, we need to use the one provided by the browser
        return window.Intl.DateTimeFormat().resolvedOptions().timeZone
    },

    locale: (state, getters) => {
        const currentUser = getters["profile/currentUser"]
        if (currentUser) {
            let language = currentUser.language
            if (language == "ct") language = "ca"
            return language
        }
        const locales = getBrowserPreferredLocales()
        const availableLanguages = state.configs.languagesToSelect || []
        const fallbackLocale =
            locales.find(l => availableLanguages.includes(l)) ||
            import.meta.env.VITE_DEFAULT_LOCALE
        return fallbackLocale
    },

    userMenuBlocks:
        (state, getters) =>
        ({ path, showSupportMailbox, openNavigationHelp }) => {
            const participantPanelRoute = state.structure.links.find(
                l => l.default
            )?.url
            const {
                biDashboardUrl,
                hasBookmarks,
                hasCalendar,
                hasSupportMailbox,
                navigationHelpUrl,
                v2AdminUrl,
                v2BIReportsUrl,
                v2CampusOknUrl,
                v2DirectoryUrl,
                v2ForumModeratorUrl,
                v2ObserverUrl,
                v2TeacherUrl,
            } = state.platform

            const currentUser = getters["profile/currentUser"]
            const hasSupervisorPanel = currentUser?.hasSupervisorPanel
            const hasAdminPanel =
                currentUser?.hasAdminPanel && getters["admin/hasAdminPanel"]

            const myBlock = [
                hasBookmarks && {
                    name: "bookmarks",
                    to: "/bookmarks",
                    text: "common.myBookmarks",
                    icon: "bookmark",
                },
                hasCalendar && {
                    name: "calendar",
                    to: "/calendar",
                    text: "common.myCalendar",
                    icon: "calendar",
                },
                currentUser?.hasV2Reports && {
                    name: "reports",
                    to: "/reports",
                    text: "v2Reports.v2Reports",
                    icon: "chart-line",
                },
                v2CampusOknUrl && {
                    href: v2CampusOknUrl,
                    text: "common.campusAccess",
                    icon: "webcam",
                },
                biDashboardUrl && {
                    name: "bi-dashboard",
                    to: "/bi-dashboard",
                    text: "common.dashboardPowerBi",
                    icon: "chart-line",
                },
            ].filter(Boolean)

            const supportBlock = [
                currentUser?.hasSloTools && {
                    name: "slo",
                    to: "/slo",
                    text: "slotools.sloTools",
                    icon: "support",
                },
                hasSupportMailbox && {
                    action: showSupportMailbox,
                    text: "common.supportMailbox",
                    icon: "headphones",
                },
                {
                    name: "faqs",
                    to: "/faqs",
                    text: "common.faqs",
                    icon: "light",
                },
                navigationHelpUrl && {
                    name: "navigation-help",
                    action: openNavigationHelp,
                    text: "common.navigationHelp",
                    icon: "question-circle",
                },
            ].filter(Boolean)

            const inSupervisorPanel = path.includes("supervisor")
            const configurationUrl = import.meta.env.VITE_API_BASE_URL.replace(
                "api",
                "admin/settings"
            )

            const externalBlock = [
                hasSupervisorPanel &&
                    inSupervisorPanel && {
                        name: "participant-role",
                        to: participantPanelRoute,
                        text: "supervisor.participantRole",
                    },
                hasSupervisorPanel &&
                    !inSupervisorPanel && {
                        name: "supervisor",
                        to: "/supervisor",
                        text: "common.supervisorPanel",
                    },
                v2AdminUrl && {
                    href: v2AdminUrl,
                    text: "common.managementArea",
                },
                hasAdminPanel && {
                    href: configurationUrl,
                    text: "admin.configuration",
                    sameTab: true,
                },
                v2TeacherUrl && {
                    href: v2TeacherUrl,
                    text: "common.teacherPanel",
                },
                v2ForumModeratorUrl && {
                    href: v2ForumModeratorUrl,
                    text: "common.forumModeratorPanel",
                },
                v2ObserverUrl && {
                    href: v2ObserverUrl,
                    text: "common.observerPanel",
                },
                v2BIReportsUrl && {
                    href: v2BIReportsUrl,
                    text: "common.biReports",
                },
                v2DirectoryUrl && {
                    href: v2DirectoryUrl,
                    text: "common.directory",
                },
            ]
                .filter(Boolean)
                .map(link => {
                    return {
                        ...link,
                        icon: "rol",
                    }
                })

            const blocks = {}
            if (externalBlock.length) blocks["external"] = externalBlock
            if (myBlock.length) blocks["me"] = myBlock
            if (supportBlock.length) blocks["support"] = supportBlock
            return blocks
        },

    defaultEntryPoint: state => {
        return state.platform.defaultEntryPoint
    },

    showLoginLogo: state => {
        const { hideLogos = [] } = state.configs
        return !(hideLogos.includes("platform") || hideLogos.includes("login"))
    },

    showMainLogo: state => {
        const { hideLogos = [] } = state.configs
        return !hideLogos.includes("platform")
    },

    hasFinder: state => {
        return state.platform.entitiesFinder.length > 0
    },
}

export const mutations = {
    pushNotification(state, notification) {
        const { info, title } = notification
        leaveBreadcrumb(`[pushNotification]: title: ${title}; info: ${info}`)
        state.notifications.push(notification)
    },

    removeNotification(state, id) {
        const idx = state.notifications.findIndex(n => n.id === id)
        if (idx === -1) return
        state.notifications.splice(idx, 1)
    },

    setConfigs(state, configs) {
        state.configs = configs
    },

    setChatbot(state, chatbot) {
        state.chatbot = chatbot
    },

    setFeatureFlags(state, featureFlags) {
        state.featureFlags = featureFlags
    },

    clearPlatform(state) {
        state.platform.allowedFiles = []
        state.platform.originalUser = null
        state.platform.alertFields = []
    },

    setPlatform(state, props) {
        state.platform = { ...state.platform, ...props }
    },

    setAlertFields(state, alertFields) {
        if (!alertFields) return
        state.platform.alertFields = alertFields
    },

    setVersionOutdated(state) {
        state.versionOutdated = true
    },

    setCurrTop(state, currTop) {
        state.currTop = currTop
    },

    setLastSpace(state, spaceId) {
        state.lastSpace = spaceId
    },

    setNavigation(state, navigation) {
        state.navigation.push(navigation)
    },

    setBearerToken(state, token) {
        state.bearerToken = token
    },

    clearNavigation(state) {
        state.navigation = []
    },
}

export function makeActions(
    ctx,
    loginService,
    assign = window.location.assign.bind(window.location)
) {
    return {
        async login(
            { commit, dispatch },
            { username, password, recaptcha_token }
        ) {
            commit("setBearerToken", undefined)
            await dispatch("clearStore")
            try {
                const response = await loginService.login({
                    username,
                    password,
                    recaptcha_token,
                })
                if (response.twoFactorAuthenticationAttemptUrl) {
                    dispatch("authentication/saveData", response)
                    return response
                } else {
                    const user = await dispatch("setup", response)
                    dispatch("cookies/save")
                    return user
                }
            } catch (error) {
                if (error instanceof ApiError) {
                    const { isBlocked = false, showCaptcha = false } =
                        error.response?.data || {}
                    commit("setPlatform", {
                        loginStatus: { isBlocked, showCaptcha },
                    })
                }
                dispatch("pushNotification", {
                    title: error.message,
                    type: "danger",
                })
            }
        },

        async welcomeBack({ commit, dispatch }) {
            // TODO the horror
            const params = parseQueryString(
                document.location.href.split("?")[1]
            )
            if (params.token) commit("setBearerToken", params.token)

            // Give time for the store plugins to make their updates.
            // Otherwise the next request to /init won't have the right headers.
            await nextTick()

            const response = await loginService.init()
            const user = await dispatch("setup", response)
            return user
        },

        async setup({ getters, commit, dispatch }, payload) {
            const {
                alertFields,
                aliveInterval = 60,
                allowedFiles = [],
                backgroundImages,
                biDashboardUrl,
                certificate = null,
                chatbot = null,
                configs = {},
                defaultEntryPoint = null,
                entitiesFinder,
                featureFlags,
                hasBookmarks,
                hasCalendar,
                hasMailbox,
                hasSupportMailbox,
                loginStatus = {},
                navigationHelpUrl,
                originalUser = null,
                platform,
                platformCode,
                platformUrl,
                scormPlayerUrl,
                theme = {},
                tokenInfo,
                translationsHash = "",
                unreadMessagesInterval = 120,
                user,
                v2AdminUrl,
                v2BIReportsUrl,
                v2CampusOknUrl,
                v2DirectoryUrl,
                v2ForumModeratorUrl,
                v2ObserverUrl,
                v2ProviderUrl,
                v2TeacherUrl,
                v2Url,
            } = payload

            const { hasSupervisorPanel, hasAdminPanel } = user ?? {}

            const idx = configs.languagesToSelect?.indexOf("ct")
            if (idx > -1) {
                configs.languagesToSelect[idx] = "ca"
            }

            commit("profile/setCurrentUser", user)
            commit("setConfigs", configs)
            commit("setAlertFields", alertFields)
            commit("setPlatform", {
                aliveInterval,
                allowedFiles,
                backgroundImages,
                biDashboardUrl,
                certificate,
                code: platformCode,
                defaultEntryPoint,
                entitiesFinder,
                hasBookmarks,
                hasCalendar,
                hasMailbox,
                hasSupportMailbox,
                loginStatus,
                name: platform,
                navigationHelpUrl,
                originalUser,
                platformUrl,
                scormPlayerUrl,
                translationsHash,
                unreadMessagesInterval,
                v2AdminUrl,
                v2BIReportsUrl,
                v2CampusOknUrl,
                v2DirectoryUrl,
                v2ForumModeratorUrl,
                v2ObserverUrl,
                v2ProviderUrl,
                v2TeacherUrl,
                v2Url,
            })

            commit("setBearerToken", tokenInfo?.accessToken)

            // Give time for the store plugins to make their updates.
            // Otherwise the next request to won't have the right headers.
            await nextTick()

            dispatch("handleInstallPrompt")

            commit("setFeatureFlags", featureFlags)
            dispatch("theme/updateCustomization", theme)
            commit("setChatbot", chatbot)

            if (import.meta.env.VITE_ENABLE_BUGSNAG === "true") {
                Bugsnag.addMetadata("user", user)
                Bugsnag.addMetadata("platform", platform)
            }

            if (user) {
                await Promise.all([
                    dispatch("loadLocale", user.language),
                    dispatch("structure/refreshStructure"),
                    hasSupervisorPanel && dispatch("supervisor/init"),
                ])
                if (hasMailbox) {
                    await dispatch("messages/updateUnreadMessages")
                }
                if (hasAdminPanel) {
                    dispatch("admin/init")
                }
            } else {
                await dispatch("loadLocale", getters.locale)
            }

            const { enableServiceWorker, enableNotifications } = featureFlags
            if (enableServiceWorker && user) {
                subscribe().then(() => {
                    if (enableNotifications) {
                        dispatch(
                            "pushNotifications/refreshNotificationPermissions"
                        )
                    }
                })
            } else {
                unsubscribe()
            }

            return user
        },

        async loadLocale({ state, dispatch }, locale) {
            try {
                const { translationsHash } = state.platform
                return await loadLocale({ ctx, locale, translationsHash })
            } catch (_) {
                if (locale != import.meta.env.VITE_DEFAULT_LOCALE) {
                    // These two messages are not internationalized on purpose.
                    // If we launch the app with an invalid language, we don't have a locale loaded.
                    // It's a weird edge case.
                    dispatch("pushNotification", {
                        title: `Error loading language: ${locale}. Trying to load: ${import.meta.env.VITE_DEFAULT_LOCALE} ...`,
                        type: "danger",
                    })
                    return await dispatch(
                        "loadLocale",
                        import.meta.env.VITE_DEFAULT_LOCALE
                    )
                } else {
                    dispatch("pushNotification", {
                        title: `Error loading language: ${locale}.`,
                        type: "danger",
                    })
                    return false
                }
            }
        },

        async logoutNoRedirect({ commit, dispatch }) {
            const { url, message, type } = await loginService.logout()

            await dispatch("clearStore")
            commit("setBearerToken", undefined)

            return { url, message, type }
        },

        async logout({ dispatch }, { router, query }) {
            const { url, message, type } = await dispatch("logoutNoRedirect")
            logoutRedirection({ router, query, url, message, type, assign })
        },

        clearStore({ commit }) {
            commit("admin/clearModule")
            commit("calendar/clearModule")
            commit("clearNavigation")
            commit("clearPlatform")
            commit("setFeatureFlags", {})

            commit("communication/clearModule")
            commit("lists/clearModule")
            commit("lms/clearModule")
            commit("messages/clearModule")
            commit("profile/clearModule")
            commit("pushNotifications/clearModule")
            commit("structure/clearModule")
            commit("supervisor/clearModule")
            commit("finder/clearModule")
        },

        /**
         * notification can contain the following fields:
         * @title String, (optional)
         * @type String ('info' by default), (optional)
         * @info [String, Array]
         * @showCloseButton Boolean (optional)
         * @actionText String (optional)
         * @actionCallback Function (optional)
         */

        pushNotification({ commit }, notification) {
            notification.type = notification.type || "info"
            // Long notification text requires showing the close button so the user has time to read the notification
            const maxCharacters = MIN_NOTIFICATION_LENGTH_TO_SHOW_CLOSE_BUTTON
            const showCloseButton =
                notification.showCloseButton ||
                notification.title?.length > maxCharacters ||
                notification.info?.toString().length > maxCharacters
            const id = Date.now()

            const cookedNotification = {
                id,
                ...notification,
                showCloseButton,
            }

            commit("pushNotification", cookedNotification)

            if (showCloseButton) {
                return
            }

            setTimeout(() => {
                commit("removeNotification", id)
            }, NOTICATION_CLOSING_TIME)
        },

        async setPassword(
            { commit, dispatch },
            { newPassword, repeatNewPassword }
        ) {
            try {
                const user = await loginService.loginChangePassword({
                    newPassword,
                    repeatNewPassword,
                })
                commit("profile/setCurrentUser", user)
                return user
            } catch (error) {
                dispatch("pushNotification", {
                    type: "danger",
                    title: error.message,
                })
                return false
            }
        },

        async acceptDataPolicy({ dispatch, commit }, id) {
            try {
                let user = await loginService.loginDataPolicy(id)

                commit("profile/setCurrentUser", user)
                return user
            } catch (error) {
                dispatch("pushNotification", {
                    type: "danger",
                    title: error.message,
                })
                return false
            }
        },

        handleInstallPrompt({ commit, dispatch }) {
            window.addEventListener("beforeinstallprompt", installEvent => {
                installEvent.preventDefault()
                commit("setPlatform", {
                    installEvent,
                    showInstallPrompt: true,
                })
            })

            window.addEventListener("appinstalled", () => {
                dispatch("hideInstallPrompt")
            })
        },

        hideInstallPrompt({ commit }) {
            commit("setPlatform", { showInstallPrompt: false })
        },

        installApp({ state }) {
            const { installEvent } = state.platform
            if (!installEvent) return
            installEvent.prompt()
        },

        addNavigation({ commit }, navigation) {
            commit("setNavigation", navigation)
        },
    }
}

const modules = {
    admin: adminStore,
    authentication: authenticationStore,
    calendar: calendarStore,
    communication: communicationStore,
    finder: finderStore,
    cookies: cookieStore,
    lists: listStore,
    lms: lmsStore,
    messages: messageStore,
    news: newsStore,
    profile: profileStore,
    pushNotifications: pushNotificationStore,
    selection: selectionStore,
    structure: structureStore,
    supervisor: supervisorStore,
    theme: themeStore,
}

const plugins = [AuthorizationHeaderPlugin(defaultCtx), GoogleAnalyticsPlugin]

if ("serviceWorker" in navigator && window.isSecureContext) {
    plugins.push(SWBearerTokenPlugin)
    plugins.push(SWPushNotificationsPlugin)
}

const store = createStore({
    strict: import.meta.env.VITE_VUEX_STRICT_STORE === "true",
    state: makeDefaultState(),
    getters,
    mutations,
    actions: makeActions(defaultCtx, new LoginService(defaultCtx)),
    modules,
    plugins,
})

bus.on("set-version-outdated", () => {
    store.commit("setVersionOutdated")
})

export default store
